import EventBus from 'eventing-bus';
import React, { useState } from 'react';
import Chip from '@mui/material/Chip';

// Mirrors the canonical Pipedrive address subfield list in server PipedriveFields.addressSubfields
const addressSubfields = [
    { suffix: 'subpremise', label: 'Apartment/Suite', sample: 'Apt 4B' },
    { suffix: 'street_number', label: 'House Number', sample: '7812' },
    { suffix: 'route', label: 'Street Name', sample: 'Shalimar Street' },
    { suffix: 'sublocality', label: 'District', sample: 'Miramar Park' },
    { suffix: 'locality', label: 'City', sample: 'Miramar' },
    { suffix: 'admin_area_level_1', label: 'State/Region', sample: 'FL' },
    { suffix: 'admin_area_level_2', label: 'County', sample: 'Broward County' },
    { suffix: 'country', label: 'Country', sample: 'USA' },
    { suffix: 'postal_code', label: 'ZIP/Postal Code', sample: '33023' },
    { suffix: 'formatted_address', label: 'Full Address', sample: '7812 Shalimar Street, Miramar, FL 33023, USA' }
];
const defaultLayout = ['street_number', 'route'];
const subfieldOf = suffix => addressSubfields.find(subfield => subfield['suffix'] == suffix);

// Theme tokens from static/css/style.css and app/NewMigration/index.css
const theme = { blue: '#0065ff', navy: '#01194d', border: '#e2e8f0', cardBg: '#f7f9fc', blueBg: '#f5f9ff', blueBorder: '#eff4ff', bodyFont: '"Work Sans", sans-serif', headingFont: '"DM Sans", sans-serif' };

const card = { border: `1px solid ${theme.border}`, borderRadius: 8, padding: '14px 16px', marginBottom: 14 };
const cardTitle = { fontFamily: theme.bodyFont, fontSize: 14, fontWeight: 600, color: theme.navy, marginBottom: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between' };
const cardHint = { fontFamily: theme.bodyFont, fontSize: 12, fontWeight: 400, color: '#8a94a6' };

const selectedChipSx = { fontFamily: theme.bodyFont, fontSize: 14, height: 34, backgroundColor: theme.blue, color: '#fff', '& .MuiChip-label': { display: 'flex', alignItems: 'center', gap: '7px', paddingLeft: '10px' }, '& .MuiChip-deleteIcon': { color: 'rgba(255,255,255,0.75)' }, '& .MuiChip-deleteIcon:hover': { color: '#fff' } };
const poolChipSx = { fontFamily: theme.bodyFont, fontSize: 14, height: 32, backgroundColor: '#fff', color: theme.blue, border: `1px solid ${theme.blue}`, '&:hover': { backgroundColor: 'rgba(0, 101, 255, 0.08)' } };

const DragDots = () => (
    <svg width='8' height='14' viewBox='0 0 8 14' fill='currentColor' opacity='0.65'>
        <circle cx='2' cy='2' r='1.4' /><circle cx='6' cy='2' r='1.4' />
        <circle cx='2' cy='7' r='1.4' /><circle cx='6' cy='7' r='1.4' />
        <circle cx='2' cy='12' r='1.4' /><circle cx='6' cy='12' r='1.4' />
    </svg>
);

const PinIcon = () => (
    <svg width='15' height='15' viewBox='0 0 24 24' fill={theme.blue} style={{ flexShrink: 0 }}>
        <path d='M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5z' />
    </svg>
);

const AddressComposer = ({ parentField, onSave }) => {
    const [selected, setSelected] = useState(defaultLayout);
    const [dragSuffix, setDragSuffix] = useState(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);

    const available = addressSubfields.filter(({ suffix }) => !selected.includes(suffix));
    const preview = selected.map(suffix => subfieldOf(suffix)['sample']).join(' ');

    const placeAt = (suffix, index) => {
        const updated = selected.filter(item => item != suffix);
        updated.splice(Math.min(index, updated.length), 0, suffix);
        setSelected(updated);
    };

    const onDrop = e => {
        e.preventDefault();
        if (dragSuffix) placeAt(dragSuffix, dragOverIndex ?? selected.length);
        setDragSuffix(null);
        setDragOverIndex(null);
    };

    const dragProps = suffix => ({
        draggable: true,
        onDragStart: e => { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', suffix); setDragSuffix(suffix); },
        onDragEnd: () => { setDragSuffix(null); setDragOverIndex(null); }
    });

    const submit = () => {
        if (!selected.length) return EventBus.publish('error', 'Add at least one address part!');
        const labels = selected.map(suffix => subfieldOf(suffix)['label']);
        onSave({
            label: `${parentField['parentLabel']} — Combined (${labels.join(' + ')})`,
            name: selected.map(suffix => `${parentField['parentKey']}_${suffix}`).join('+'),
            type: 'address',
            picklistValues: null,
            objectType: parentField['objectType'],
            hubspotType: parentField['hubspotType']
        });
    };

    const dropIndicator = <span style={{ width: 3, height: 30, backgroundColor: theme.blue, borderRadius: 2, flexShrink: 0 }} />;

    return (
        <div className='connect-inner'>
            <div className='payment-box'>
                <div className='title-area text-center mb-4'>
                    <h2>Configure Combined Address</h2>
                    <p className='text-simple'>Build how “{parentField['parentLabel']}” should appear in HubSpot — drag the parts into order.</p>
                </div>

                <div style={card}>
                    <div style={cardTitle}>
                        <span>Address layout</span>
                        <span style={cardHint}>drag to reorder · click × to remove</span>
                    </div>
                    <div
                        onDrop={onDrop}
                        onDragOver={e => { e.preventDefault(); if (dragOverIndex === null) setDragOverIndex(selected.length); }}
                        style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', minHeight: 58, padding: 10, borderRadius: 6, border: `1px dashed ${dragSuffix ? theme.blue : theme.border}`, backgroundColor: dragSuffix ? theme.blueBg : '#fff', transition: 'all 0.2s ease-in-out' }}
                    >
                        {selected.map((suffix, index) =>
                            <React.Fragment key={suffix}>
                                {dragSuffix && dragOverIndex === index && dropIndicator}
                                <Chip
                                    {...dragProps(suffix)}
                                    label={
                                        <>
                                            <DragDots />
                                            <span style={{ backgroundColor: 'rgba(255,255,255,0.22)', borderRadius: 9, minWidth: 18, height: 18, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 600 }}>{index + 1}</span>
                                            {subfieldOf(suffix)['label']}
                                        </>
                                    }
                                    onDelete={() => setSelected(selected.filter(item => item != suffix))}
                                    onDragOver={e => { e.preventDefault(); e.stopPropagation(); setDragOverIndex(index); }}
                                    style={{ cursor: 'grab', opacity: dragSuffix == suffix ? 0.4 : 1 }}
                                    sx={selectedChipSx}
                                />
                            </React.Fragment>
                        )}
                        {dragSuffix && dragOverIndex === selected.length && dropIndicator}
                        {!selected.length && <span style={{ color: '#8a94a6', fontFamily: theme.bodyFont, fontSize: 14, width: '100%', textAlign: 'center' }}>Drag or click fields below to add them here</span>}
                    </div>
                </div>

                <div style={{ ...card, backgroundColor: theme.cardBg }}>
                    <div style={cardTitle}><span>Available fields</span><span style={cardHint}>click or drag to add</span></div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {available.map(({ suffix, label }) =>
                            <Chip
                                key={suffix}
                                {...dragProps(suffix)}
                                label={`+ ${label}`}
                                onClick={() => setSelected([...selected, suffix])}
                                style={{ cursor: 'grab', opacity: dragSuffix == suffix ? 0.4 : 1 }}
                                sx={poolChipSx}
                            />
                        )}
                        {!available.length && <span style={{ color: '#8a94a6', fontFamily: theme.bodyFont, fontSize: 13 }}>All fields are in the layout</span>}
                    </div>
                </div>

                <div style={{ ...card, backgroundColor: theme.blueBg, border: `1px solid ${theme.blueBorder}` }}>
                    <div style={cardTitle}><span>Preview</span><span style={cardHint}>with sample Pipedrive data</span></div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <PinIcon />
                        <span style={{ fontFamily: theme.headingFont, fontSize: 16, color: theme.navy }}>{preview || '—'}</span>
                    </div>
                    <p style={{ fontFamily: theme.bodyFont, fontSize: 12, color: '#8a94a6', margin: '10px 0 0' }}>If all selected parts are empty for a record, the full Pipedrive address is used instead so the value is never blank.</p>
                </div>

                <div className='option-box justify-content-center mt-4' style={{ gap: 10 }}>
                    <button className='save-btn' onClick={submit}>Save</button>
                    <button className='save-btn' style={{ backgroundColor: 'transparent', color: theme.blue }} onClick={() => setSelected(defaultLayout)}>Use default</button>
                </div>
            </div>
        </div>
    );
};

export default AddressComposer;
